import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Modal, Form, Row, Col, Card } from 'react-bootstrap';
import { BsPlusLg, BsPencil, BsTrash, BsGear } from 'react-icons/bs';
import { toast } from 'react-toastify';
import workflowService from '../../services/workflowService';
import WorkflowSteps from './WorkflowSteps';

const Workflows = () => {
    const [workflows, setWorkflows] = useState([]);
    const [processTypes, setProcessTypes] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showStepsModal, setShowStepsModal] = useState(false);
    const [selectedWorkflow, setSelectedWorkflow] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        processTypeId: '',
        defaultSLAHours: 72,
        baselineQoS: 3,
        minimumAcceptableQoS: 2,
        weightCompliance: 0.35,
        weightQuality: 0.30,
        weightEscalation: 0.15,
        weightAging: 0.10,
        weightSuspension: 0.10,
        agingThresholdHours: 48,
        isDefault: false,
    });

    useEffect(() => {
        loadWorkflows();
        loadProcessTypes();
    }, []);

    const loadWorkflows = async () => {
        try {
            const data = await workflowService.getAllWorkflows();
            setWorkflows(data);
        } catch (error) {
            console.error('Error loading workflows:', error);
            toast.error('Failed to load workflows');
        }
    };

    const loadProcessTypes = async () => {
        try {
            const data = await workflowService.getAllProcessTypes();
            setProcessTypes(data);
        } catch (error) {
            console.error('Error loading process types:', error);
            toast.error('Failed to load process types');
        }
    };

    const handleShowModal = (workflow = null) => {
        if (workflow) {
            setFormData({
                name: workflow.name,
                description: workflow.description,
                processTypeId: workflow.processTypeId,
                defaultSLAHours: workflow.defaultSLAHours || 72,
                baselineQoS: workflow.baselineQoS || 3,
                minimumAcceptableQoS: workflow.minimumAcceptableQoS || 2,
                weightCompliance: workflow.weightCompliance || 0.35,
                weightQuality: workflow.weightQuality || 0.30,
                weightEscalation: workflow.weightEscalation || 0.15,
                weightAging: workflow.weightAging || 0.10,
                weightSuspension: workflow.weightSuspension || 0.10,
                agingThresholdHours: workflow.agingThresholdHours || 48,
                isDefault: workflow.isDefault || false,
            });
            setSelectedWorkflow(workflow);
        } else {
            setFormData({ 
                name: '', 
                description: '', 
                processTypeId: '',
                defaultSLAHours: 72,
                baselineQoS: 3,
                minimumAcceptableQoS: 2,
                weightCompliance: 0.35,
                weightQuality: 0.30,
                weightEscalation: 0.15,
                weightAging: 0.10,
                weightSuspension: 0.10,
                agingThresholdHours: 48,
                isDefault: false,
            });
            setSelectedWorkflow(null);
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedWorkflow(null);
        setFormData({ 
            name: '', 
            description: '', 
            processTypeId: '',
            defaultSLAHours: 72,
            baselineQoS: 3,
            minimumAcceptableQoS: 2,
            weightCompliance: 0.35,
            weightQuality: 0.30,
            weightEscalation: 0.15,
            weightAging: 0.10,
            weightSuspension: 0.10,
            agingThresholdHours: 48,
            isDefault: false,
        });
    };

    const handleShowStepsModal = (workflow) => {
        setSelectedWorkflow(workflow);
        setShowStepsModal(true);
    };

    const handleCloseStepsModal = () => {
        setShowStepsModal(false);
        setSelectedWorkflow(null);
        loadWorkflows(); // Reload workflows to get updated steps
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let parsedValue = value;
        
        // Parse numeric fields
        if (['defaultSLAHours', 'agingThresholdHours'].includes(name)) {
            parsedValue = parseInt(value) || 0;
        } else if (['baselineQoS', 'minimumAcceptableQoS', 'weightCompliance', 'weightQuality', 'weightEscalation', 'weightAging', 'weightSuspension'].includes(name)) {
            parsedValue = parseFloat(value) || 0;
        }
        
        setFormData(prev => ({
            ...prev,
            [name]: parsedValue
        }));
    };

    const validateWeightSum = () => {
        const totalWeight = 
            (parseFloat(formData.weightCompliance) || 0) +
            (parseFloat(formData.weightQuality) || 0) +
            (parseFloat(formData.weightEscalation) || 0) +
            (parseFloat(formData.weightAging) || 0) +
            (parseFloat(formData.weightSuspension) || 0);
        
        // Allow 0.01 tolerance for floating point errors
        const isValid = Math.abs(totalWeight - 1.0) <= 0.01;
        
        return {
            isValid,
            total: totalWeight.toFixed(2),
            warning: !isValid ? `Weight sum is ${totalWeight.toFixed(2)}, but should equal 1.0` : null
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate weight sum
        const weightValidation = validateWeightSum();
        if (!weightValidation.isValid) {
            toast.error(weightValidation.warning);
            return;
        }
        
        try {
            if (selectedWorkflow) {
                await workflowService.updateWorkflow(selectedWorkflow.id, formData);
                toast.success('Workflow updated successfully');
            } else {
                await workflowService.createWorkflow(formData);
                toast.success('Workflow created successfully');
            }
            handleCloseModal();
            loadWorkflows();
        } catch (error) {
            console.error('Error saving workflow:', error);
            toast.error('Failed to save workflow');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this workflow?')) {
            try {
                await workflowService.deleteWorkflow(id);
                toast.success('Workflow deleted successfully');
                loadWorkflows();
            } catch (error) {
                console.error('Error deleting workflow:', error);
                toast.error('Failed to delete workflow');
            }
        }
    };

    const handleSetDefault = async (workflow) => {
        try {
            await workflowService.setWorkflowAsDefault(workflow.id);
            toast.success('Workflow set as default successfully');
            loadWorkflows();
        } catch (error) {
            console.error('Error setting default workflow:', error);
            toast.error('Failed to set default workflow');
        }
    };

    const getProcessTypeName = (processTypeId) => {
        const processType = processTypes.find(pt => pt.id === processTypeId);
        return processType ? processType.name : 'Unknown';
    };

    return (
        <Container fluid>
            <Row className="mb-3">
                <Col className="d-flex justify-content-end">
                    <Button 
                        variant="primary" 
                        onClick={() => handleShowModal()}
                    >
                        <BsPlusLg /> Add Workflow
                    </Button>
                </Col>
            </Row>

            <Row xs={1} md={2} lg={3} className="g-4">
                {workflows.map((workflow) => (
                    <Col key={workflow.id}>
                        <Card>
                            <Card.Body>
                                <Card.Title className="d-flex justify-content-between align-items-center">
                                    {workflow.name}
                                    <div>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => handleShowModal(workflow)}
                                        >
                                            <BsPencil />
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleDelete(workflow.id)}
                                        >
                                            <BsTrash />
                                        </Button>
                                    </div>
                                </Card.Title>
                                <Card.Subtitle className="mb-2 text-muted">
                                    {getProcessTypeName(workflow.processTypeId)}
                                </Card.Subtitle>
                                <Card.Text>{workflow.description}</Card.Text>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex gap-2 align-items-center">
                                        <span className={`badge bg-${workflow.isActive ? 'success' : 'secondary'}`}>
                                            {workflow.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                        <Form.Check
                                            type="checkbox"
                                            checked={workflow.isDefault}
                                            onChange={() => handleSetDefault(workflow)}
                                            label="Default"
                                            style={{ margin: 0 }}
                                        />
                                    </div>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleShowStepsModal(workflow)}
                                    >
                                        <BsGear className="me-1" />
                                        Configure Steps
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Workflow Edit/Create Modal */}
            <Modal show={showModal} onHide={handleCloseModal}>
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {selectedWorkflow ? 'Edit Workflow' : 'New Workflow'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Process Type</Form.Label>
                            <Form.Select
                                name="processTypeId"
                                value={formData.processTypeId}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="">Select Process Type</option>
                                {processTypes.map(pt => (
                                    <option key={pt.id} value={pt.id}>
                                        {pt.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>

                        {/* SLA Configuration Section */}
                        <div className="mt-4 p-3 bg-light rounded border">
                            <h6 className="mb-3">SLA Configuration</h6>
                            <Form.Group className="mb-3">
                                <Form.Label>Default SLA Hours</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="defaultSLAHours"
                                    value={formData.defaultSLAHours}
                                    onChange={handleInputChange}
                                    min="1"
                                    max="2160"
                                    required
                                />
                                <Form.Text className="text-muted">Total resolution time (hours)</Form.Text>
                            </Form.Group>
                        </div>

                        {/* Quality of Service Section */}
                        <div className="mt-3 p-3 bg-light rounded border">
                            <h6 className="mb-3">Quality of Service (QoS)</h6>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Baseline QoS Target (1-5)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="baselineQoS"
                                            value={formData.baselineQoS}
                                            onChange={handleInputChange}
                                            min="1"
                                            max="5"
                                            step="0.1"
                                            required
                                        />
                                        <Form.Text className="text-muted">Expected quality score</Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Minimum Acceptable QoS (1-5)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="minimumAcceptableQoS"
                                            value={formData.minimumAcceptableQoS}
                                            onChange={handleInputChange}
                                            min="1"
                                            max="5"
                                            step="0.1"
                                            required
                                        />
                                        <Form.Text className="text-muted">Minimum before penalties</Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>

                        {/* Efficiency Weights Section */}
                        <div className="mt-3 p-3 bg-light rounded border">
                            <h6 className="mb-3">Efficiency Formula Weights (0-1)</h6>
                            <div className="text-muted small mb-3">
                                Adjust how each factor impacts efficiency score (sum should equal 1.0)
                            </div>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Compliance Weight (w1)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="weightCompliance"
                                            value={formData.weightCompliance}
                                            onChange={handleInputChange}
                                            min="0"
                                            max="1"
                                            step="0.01"
                                            required
                                        />
                                        <Form.Text className="text-muted">Default: 0.35</Form.Text>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Quality Weight (w2)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="weightQuality"
                                            value={formData.weightQuality}
                                            onChange={handleInputChange}
                                            min="0"
                                            max="1"
                                            step="0.01"
                                            required
                                        />
                                        <Form.Text className="text-muted">Default: 0.30</Form.Text>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Escalation Penalty (w3)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="weightEscalation"
                                            value={formData.weightEscalation}
                                            onChange={handleInputChange}
                                            min="0"
                                            max="1"
                                            step="0.01"
                                            required
                                        />
                                        <Form.Text className="text-muted">Default: 0.15</Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Aging Penalty (w4)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="weightAging"
                                            value={formData.weightAging}
                                            onChange={handleInputChange}
                                            min="0"
                                            max="1"
                                            step="0.01"
                                            required
                                        />
                                        <Form.Text className="text-muted">Default: 0.10</Form.Text>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Suspension Penalty (w5)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="weightSuspension"
                                            value={formData.weightSuspension}
                                            onChange={handleInputChange}
                                            min="0"
                                            max="1"
                                            step="0.01"
                                            required
                                        />
                                        <Form.Text className="text-muted">Default: 0.10</Form.Text>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Aging Threshold (hours)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="agingThresholdHours"
                                            value={formData.agingThresholdHours}
                                            onChange={handleInputChange}
                                            min="1"
                                            max="720"
                                            required
                                        />
                                        <Form.Text className="text-muted">Flag ticket as aging if exceeds</Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            {/* Weight Sum Indicator */}
                            <div className={`mt-3 p-2 rounded ${validateWeightSum().isValid ? 'bg-success bg-opacity-10 border border-success' : 'bg-danger bg-opacity-10 border border-danger'}`}>
                                <small className={validateWeightSum().isValid ? 'text-success' : 'text-danger'}>
                                    <strong>Weight Sum: {validateWeightSum().total}</strong>
                                    {validateWeightSum().isValid ? ' ✓ Valid' : ` ✗ ${validateWeightSum().warning}`}
                                </small>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit">
                            {selectedWorkflow ? 'Update' : 'Create'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Workflow Steps Modal */}
            <Modal 
                show={showStepsModal} 
                onHide={handleCloseStepsModal}
                size="xl"
                fullscreen="lg-down"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        Configure Steps: {selectedWorkflow?.name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {selectedWorkflow && (
                        <WorkflowSteps 
                            workflowId={selectedWorkflow.id}
                            onClose={handleCloseStepsModal}
                        />
                    )}
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default Workflows; 